import { AlertTriangle } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { config } from '@/app/config/env';
import { logger } from '@/lib/logger';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Custom fallback; receives a reset callback to retry the subtree. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /**
   * Changing any value in this array resets the boundary. Pass the route
   * pathname so a navigation clears a previously crashed screen.
   */
  resetKeys?: readonly unknown[];
  /** Hook for reporting to an external error tracker. */
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches render-phase errors so one broken subtree does not blank the app.
 *
 * Must be a class component: React exposes no hook equivalent of
 * `componentDidCatch`.
 *
 * Note the scope limits — error boundaries do not catch errors thrown in event
 * handlers, in async callbacks, or during SSR. Those paths handle their own
 * failures through the API error layer.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Redacted by the logger before it reaches the console.
    logger.error('Render error caught by boundary', {
      error,
      componentStack: info.componentStack,
    });

    this.props.onError?.(error, info);
  }

  override componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    const { resetKeys } = this.props;
    if (this.state.error === null || !resetKeys) return;

    const previous = prevProps.resetKeys;
    if (previous?.length !== resetKeys.length) {
      this.reset();
      return;
    }

    if (resetKeys.some((key, index) => !Object.is(key, previous[index]))) {
      this.reset();
    }
  }

  private readonly reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    const { error } = this.state;
    const { children, fallback } = this.props;

    if (error === null) return children;
    if (fallback) return fallback(error, this.reset);

    return (
      <div
        role="alert"
        className="flex min-h-64 flex-col items-center justify-center gap-4 p-8 text-center"
      >
        <AlertTriangle className="text-destructive size-10" aria-hidden="true" />

        <div className="space-y-1">
          <h2 className="text-lg font-semibold">Something went wrong</h2>
          <p className="text-muted-foreground max-w-md text-sm">
            This part of the page failed to load. Try again, and reload if the problem continues.
          </p>
        </div>

        {/*
          The message and stack are shown only outside production. In a
          production build they would expose internal module paths and logic
          to any user who triggered the crash.
        */}
        {config.isDevelopment && (
          <pre className="bg-muted text-muted-foreground max-w-full overflow-x-auto rounded-md p-3 text-left text-xs">
            {error.message}
          </pre>
        )}

        <Button onClick={this.reset} variant="outline">
          Try again
        </Button>
      </div>
    );
  }
}
